import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  LogOut,
  Sparkles,
  Sun,
  Moon,
  Menu,
  ArrowLeft,
  Settings,
  Sparkle,
  ExternalLink,
  ChevronDown,
  Star
} from "lucide-react";
import { NotificationDropdown } from "./NotificationDropdown";
import { useNotifications } from "@/hooks/useNotifications";
import { useCachedAvatar } from "@/hooks/useCachedAvatar";
import { supabase } from "@/integrations/supabase/client";

interface AccountSidebarProps {
  trigger?: React.ReactNode;
  onNavigate?: () => void;
  onOpenSettings?: () => void;
}

export function AccountSidebar({ trigger, onNavigate, onOpenSettings }: AccountSidebarProps) {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { unreadCount } = useNotifications();
  const { avatarUrl } = useCachedAvatar();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(null);

  // Check if we're in an organization view or private beta
  const isInOrganization = location.pathname.includes('/org/');
  const isPrivateBeta = location.pathname.includes('/private-beta');
  const organizationId = params.organizationId;

  // Fetch display name from profiles table
  useEffect(() => {
    const fetchDisplayName = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.display_name) {
        setProfileDisplayName(data.display_name);
      }
    };
    fetchDisplayName();
  }, [user]);

  const getUserDisplayName = () => {
    if (profileDisplayName) return profileDisplayName;
    const email = user?.email || '';
    return email.split('@')[0];
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    return displayName.slice(0, 2).toUpperCase();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
    onNavigate?.();
  };

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
    onNavigate?.();
  };

  const handleProfileSettings = () => {
    if (onOpenSettings) {
      onOpenSettings();
    } else if (isInOrganization || isPrivateBeta) {
      navigate('/dashboard#account');
    } else {
      const section = document.querySelector('#account');
      section?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
    onNavigate?.();
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="flex items-center gap-2 relative">
      Hi, {getUserDisplayName()}
      <ChevronDown className="h-4 w-4" />
      {unreadCount > 0 && (
        <Badge 
          variant="destructive" 
          className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </Badge>
      )}
    </Button>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || defaultTrigger}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-96 p-0">
        <div className="flex flex-col h-full">
          {/* User Profile Header */}
          <div className="p-6 border-b border-border">
            {/* Move everything down to align with notification bell */}
            <div className="pt-8">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={avatarUrl || undefined} alt={getUserDisplayName()} />
                  <AvatarFallback>{getUserInitials()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-foreground truncate">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <NotificationDropdown />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 p-6 space-y-4">
            {isInOrganization ? (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-lg h-12"
                  onClick={() => handleNavigation(`/dashboard/org/${organizationId}/queues`)}
                >
                  <Menu className="mr-3 h-5 w-5" />
                  Queues
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-lg h-12"
                  onClick={() => handleNavigation(`/dashboard/org/${organizationId}/apps`)}
                >
                  <Settings className="mr-3 h-5 w-5" />
                  Apps
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-lg h-12"
                  onClick={() => handleNavigation('/dashboard')}
                >
                  <ArrowLeft className="mr-3 h-5 w-5" />
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-lg h-12"
                  onClick={() => {
                    if (location.pathname !== '/dashboard') {
                      navigate('/dashboard#organizations');
                    } else {
                      const section = document.querySelector('#organizations');
                      section?.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                >
                  <Menu className="mr-3 h-5 w-5" />
                  Workspaces
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-lg h-12"
                  onClick={() => {
                    if (location.pathname !== '/dashboard') {
                      navigate('/dashboard#favorites');
                    } else {
                      const section = document.querySelector('#favorites');
                      section?.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                >
                  <Star className="mr-3 h-5 w-5" />
                  Favorites
                </Button>
              </>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="p-6 border-t border-border space-y-4">
            {/* Profile Settings */}
            <Button
              variant="ghost"
              className="w-full justify-start text-base h-12"
              onClick={handleProfileSettings}
            >
              <Settings className="mr-3 h-5 w-5" />
              Settings
            </Button>


            {/* See What's New */}
            <Button
              variant="ghost"
              className="w-full justify-start text-base h-12"
              asChild
            >
              <a
                data-canny-link
                href="https://help.queueos.com/collection/changelog"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                <Sparkles className="mr-3 h-5 w-5" />
                See what's new
                <ExternalLink className="ml-auto h-4 w-4" />
              </a>
            </Button>

            {/* Sign Out */}
            <Button
              variant="ghost"
              className="w-full justify-start text-base h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}